<template>
  <CModal
      :show.sync="visible"
      size="lg"
      :title="modalTitle"
      @hidden="onHidden"
      :close-on-backdrop="false"
  >
    <!-- Warning Banner -->
    <CAlert color="warning" class="mb-4">
      <div class="d-flex align-items-center">
        <CIcon name="cilWarning" size="xl" class="mr-3"/>
        <div>
          <strong>
            <translate translate-context="collaborators.virtualusers">
              ⚠️ SAVE THESE CREDENTIALS NOW!
            </translate>
          </strong>
          <div class="mt-1">
            <translate translate-context="collaborators.virtualusers">
              They will NOT be shown again. Make sure to copy and save them securely.
            </translate>
          </div>
        </div>
      </div>
    </CAlert>

    <!-- Single Credential Display -->
    <div v-if="!isMultiple && credentials" class="credentials-display">
      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Email</translate>
        </CLabel>
        <CInputGroup>
          <CInput :value="credentials.email" readonly/>
          <CInputGroupAppend>
            <CButton color="secondary" @click="copyField(credentials.email)">
              <CIcon name="cilCopy"/>
            </CButton>
          </CInputGroupAppend>
        </CInputGroup>
      </CFormGroup>

      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Password</translate>
        </CLabel>
        <CInputGroup>
          <CInput :value="credentials.password" :type="showPassword ? 'text' : 'password'" readonly/>
          <CInputGroupAppend>
            <CButton color="secondary" @click="showPassword = !showPassword">
              <CIcon :name="showPassword ? 'cilEyeSlash' : 'cilEye'"/>
            </CButton>
            <CButton color="secondary" @click="copyField(credentials.password)">
              <CIcon name="cilCopy"/>
            </CButton>
          </CInputGroupAppend>
        </CInputGroup>
      </CFormGroup>

      <CFormGroup>
        <CLabel>
          <translate translate-context="collaborators.virtualusers">Domain</translate>
        </CLabel>
        <CInput :value="credentials.domain" readonly/>
      </CFormGroup>
    </div>

    <!-- Multiple Credentials Display (Bulk Reset) -->
    <div v-if="isMultiple && credentialsArray.length > 0" class="credentials-table">
      <CDataTable
          :items="credentialsArray"
          :fields="tableFields"
          hover
          bordered
          small
      >
        <template #password="{item}">
          <td>
            <code class="text-monospace">{{ item.password }}</code>
          </td>
        </template>
        <template #email="{item}">
          <td>
            <small class="text-muted">{{ item.email }}</small>
          </td>
        </template>
      </CDataTable>
    </div>

    <!-- Copy All Section -->
    <div class="mt-4 bg-light p-3 rounded">
      <div class="d-flex justify-content-between align-items-center">
        <span class="font-weight-bold">
          <translate translate-context="collaborators.virtualusers">
            {{ isMultiple ? 'Copy All Credentials' : 'Copy Credentials' }}
          </translate>
        </span>
        <CButton color="primary" @click="copyAll">
          <CIcon name="cilCopy" class="mr-2"/>
          <translate translate-context="collaborators.virtualusers">Copy All</translate>
        </CButton>
      </div>
      <small class="text-muted">
        <translate translate-context="collaborators.virtualusers">
          Formatted text ready to share via WhatsApp or secure messaging
        </translate>
      </small>
    </div>

    <template #footer>
      <CButton color="primary" @click="close">
        <translate translate-context="collaborators.virtualusers">Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import {mapGetters} from 'vuex'

export default {
  name: 'VirtualUserCredentialsModal',

  data() {
    return {
      visible: false,
      credentials: null,
      showPassword: false,
      tableFields: [
        {
          key: 'email',
          label: this.$pgettext('collaborators.virtualusers', 'Email')
        },
        {
          key: 'password',
          label: this.$pgettext('collaborators.virtualusers', 'Password')
        }
      ]
    }
  },

  computed: {
    ...mapGetters('event', [
      'currentEventName'
    ]),

    isMultiple() {
      return Array.isArray(this.credentials)
    },

    credentialsArray() {
      return this.isMultiple ? this.credentials : []
    },

    modalTitle() {
      return this.$pgettext('collaborators.virtualusers', 'Virtual User Credentials')
    }
  },

  methods: {
    show(credentials) {
      this.credentials = credentials
      this.visible = true
      this.showPassword = false
    },

    close() {
      this.visible = false
    },

    onHidden() {
      // Clear credentials from memory when modal closes
      this.credentials = null
      this.showPassword = false

      // Clear from Vuex store
      this.$store.dispatch('collaborators/clearVirtualUserCredentials')
    },

    copyField(text) {
      this.copyToClipboard(text)
      this.notifySuccess(
          this.$pgettext('collaborators.virtualusers', 'Copied to clipboard')
      )
    },

    copyAll() {
      const text = this.isMultiple
          ? this.formatMultipleCredentials()
          : this.formatSingleCredential()

      this.copyToClipboard(text)
      this.notifySuccess(
          this.$pgettext('collaborators.virtualusers', 'All credentials copied to clipboard')
      )
    },

    formatSingleCredential() {
      if (!this.credentials) return ''

      const serviceName = this.currentEventName || 'Virtual User'

      return `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${serviceName}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Email: ${this.credentials.email}
Password: ${this.credentials.password}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  Keep this password secure
`.trim()
    },

    formatMultipleCredentials() {
      if (!this.credentialsArray.length) return ''

      const serviceName = this.currentEventName || 'Virtual Users'
      let text = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${serviceName}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

`
      this.credentialsArray.forEach((cred) => {
        text += `Email: ${cred.email}
Password: ${cred.password}

`
      })

      text += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  Keep these passwords secure
`
      return text.trim()
    },

    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
    }
  }
}
</script>

<style scoped>
.credentials-display {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.credentials-table {
  max-height: 400px;
  overflow-y: auto;
}

code.text-monospace {
  background-color: #e9ecef;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
}
</style>
